import { CENTINELA_MESSAGE_TONES } from "../../../features/centinela/types";
import type {
  CentinelaMessageTone,
  CentinelaNarrative,
  CentinelaSummary,
  CentinelaSummaryPanel,
} from "../../../features/centinela/types";

export interface MonitoringNarrative {
  title: string;
  body: string;
  tone: CentinelaMessageTone;
}

export interface MonitoringCalls {
  total: number;
  answered: number;
  answerRate: number;
}

export interface MonitoringCases {
  total: number;
  open: number;
  resolvedWithoutCall: number;
  requiresHuman: number;
}

export interface MonitoringSeriesPoint {
  date: string;
  value: number;
}

export interface MonitoringSeries {
  available: boolean;
  reason: string | null;
  total: number;
  points: MonitoringSeriesPoint[];
}

export interface MonitoringTripCounts {
  total: number;
  inProgress: number;
  scheduled: number;
  completed: number;
  cancelled: number;
  unknown: number;
}

export interface MonitoringPerformance {
  checks: { total: number; ok: number; pct: number };
  reactionSeconds: number | null;
  realIncidents: number;
}

export interface MonitoringCustody {
  tripId: string;
  deviceId: string;
  title: string | null;
  profile: string | null;
  startedAt: string | null;
  endsAt: string | null;
  progressPercent: number | null;
  progressIsEstimated: boolean;
}

export interface MonitoringPanel {
  hasPanel: boolean;
  windowDays: number;
  calculatedAt: string | null;
  alerts: MonitoringSeries;
  kilometers: MonitoringSeries;
  trips: MonitoringTripCounts;
  availableRoutes: number;
  performance: MonitoringPerformance | null;
  custodies: MonitoringCustody[];
}

export interface MonitoringOverview {
  hasData: boolean;
  windowHours: number;
  generatedAt: string | null;
  watchedUnits: number;
  geofences: number;
  trips: number;
  calls: MonitoringCalls;
  cases: MonitoringCases;
  narrative: MonitoringNarrative | null;
  panel: MonitoringPanel;
}

const DEFAULT_WINDOW_HOURS = 24;

const DEFAULT_PANEL_DAYS = 7;

const REASON_KEYS = ["motivo", "razon", "detalle", "mensaje", "error"] as const;

const NARRATIVE_TONE_ALIASES: Record<string, CentinelaMessageTone> = {
  informativo: "info",
  normal: "info",
  tranquilo: "info",
  ok: "info",
  warning: "warn",
  atencion: "warn",
  "atención": "warn",
  advertencia: "warn",
  precaucion: "warn",
  "precaución": "warn",
  pendiente: "warn",
  alerta: "alert",
  critico: "alert",
  "crítico": "alert",
  grave: "alert",
  urgente: "alert",
};

const isMessageTone = (value: string): value is CentinelaMessageTone =>
  (CENTINELA_MESSAGE_TONES as readonly string[]).includes(value);

export const toMessageTone = (
  value: string | null | undefined,
): CentinelaMessageTone => {
  if (!value) return "info";

  const key = value.trim().toLowerCase();
  if (isMessageTone(key)) return key;

  return NARRATIVE_TONE_ALIASES[key] ?? "info";
};

const toAnswerRate = (rate: number | null, total: number, answered: number): number => {
  if (typeof rate === "number" && Number.isFinite(rate)) {
    return Math.min(Math.max(Math.round(rate), 0), 100);
  }
  return total > 0 ? Math.round((answered / total) * 100) : 0;
};

const toNarrative = (
  value: CentinelaNarrative | null | undefined,
): MonitoringNarrative | null => {
  if (!value) return null;

  const title = value.titulo?.trim() ?? "";
  const body = value.cuerpo?.trim() || value.texto?.trim() || "";
  if (title.length === 0 && body.length === 0) return null;

  return { title, body, tone: toMessageTone(value.tono) };
};

const toReason = (raw: unknown): string | null => {
  const record = (raw ?? {}) as Record<string, unknown>;
  const found = REASON_KEYS.map((key) => record[key]).find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  return typeof found === "string" ? found.trim() : null;
};

const toCount = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const emptySeries = (): MonitoringSeries => ({
  available: true,
  reason: null,
  total: 0,
  points: [],
});

const toSeries = (
  raw: { disponible?: boolean; total?: number; total_km?: number } | undefined,
  points: { dia: string; value: unknown }[],
  total: unknown,
): MonitoringSeries => {
  const available = raw?.disponible !== false;
  return {
    available,
    reason: available ? null : toReason(raw),
    total: toCount(total),
    points: points.map((point) => ({ date: point.dia, value: toCount(point.value) })),
  };
};

const emptyTripCounts = (): MonitoringTripCounts => ({
  total: 0,
  inProgress: 0,
  scheduled: 0,
  completed: 0,
  cancelled: 0,
  unknown: 0,
});

export function emptyPanel(): MonitoringPanel {
  return {
    hasPanel: false,
    windowDays: DEFAULT_PANEL_DAYS,
    calculatedAt: null,
    alerts: emptySeries(),
    kilometers: emptySeries(),
    trips: emptyTripCounts(),
    availableRoutes: 0,
    performance: null,
    custodies: [],
  };
}

function mapPanel(panel: CentinelaSummaryPanel | undefined): MonitoringPanel {
  if (!panel) return emptyPanel();

  const checks = panel.rendimiento?.verificaciones;
  const checksTotal = toCount(checks?.total);
  const checksOk = toCount(checks?.exitosas);

  return {
    hasPanel: true,
    windowDays: toCount(panel.ventana_dias) || DEFAULT_PANEL_DAYS,
    calculatedAt: panel.calculado_en ?? null,
    alerts: toSeries(
      panel.alertas_historicas,
      (panel.alertas_historicas?.por_dia ?? []).map((point) => ({
        dia: point.dia,
        value: point.alertas,
      })),
      panel.alertas_historicas?.total,
    ),
    kilometers: toSeries(
      panel.kilometros,
      (panel.kilometros?.por_dia ?? []).map((point) => ({ dia: point.dia, value: point.km })),
      panel.kilometros?.total_km,
    ),
    trips: {
      total: toCount(panel.viajes?.total),
      inProgress: toCount(panel.viajes?.en_curso),
      scheduled: toCount(panel.viajes?.programado),
      completed: toCount(panel.viajes?.completado),
      cancelled: toCount(panel.viajes?.cancelado),
      unknown: toCount(panel.viajes?.sin_estado),
    },
    availableRoutes: toCount(panel.rutas_disponibles),
    performance: panel.rendimiento
      ? {
          checks: {
            total: checksTotal,
            ok: checksOk,
            pct:
              typeof checks?.pct === "number" && Number.isFinite(checks.pct)
                ? Math.round(checks.pct)
                : checksTotal > 0
                  ? Math.round((checksOk / checksTotal) * 100)
                  : 0,
          },
          reactionSeconds:
            typeof panel.rendimiento.reaccion_seg === "number" &&
            Number.isFinite(panel.rendimiento.reaccion_seg)
              ? panel.rendimiento.reaccion_seg
              : null,
          realIncidents: toCount(panel.rendimiento.incidentes_reales),
        }
      : null,
    custodies: (panel.custodias ?? []).map((custody) => ({
      tripId: custody.trip_id,
      deviceId: custody.device_id,
      title: custody.titulo?.trim() || null,
      profile: custody.perfil?.trim() || null,
      startedAt: custody.iniciado_en ?? null,
      endsAt: custody.termina_en ?? null,
      progressPercent:
        typeof custody.avance_pct === "number" && Number.isFinite(custody.avance_pct)
          ? Math.min(Math.max(Math.round(custody.avance_pct), 0), 100)
          : null,
      progressIsEstimated: custody.avance_es_estimado === true,
    })),
  };
}

export function emptyOverview(): MonitoringOverview {
  return {
    hasData: false,
    windowHours: DEFAULT_WINDOW_HOURS,
    generatedAt: null,
    watchedUnits: 0,
    geofences: 0,
    trips: 0,
    calls: { total: 0, answered: 0, answerRate: 0 },
    cases: { total: 0, open: 0, resolvedWithoutCall: 0, requiresHuman: 0 },
    narrative: null,
    panel: emptyPanel(),
  };
}

export function mapCentinelaSummary(
  summary: CentinelaSummary | undefined,
): MonitoringOverview {
  if (!summary) return emptyOverview();

  const total = summary.calls?.total ?? 0;
  const answered = summary.calls?.answered ?? 0;

  return {
    hasData: true,
    windowHours: summary.window_hours ?? DEFAULT_WINDOW_HOURS,
    generatedAt: summary.generated_at ?? null,
    watchedUnits: summary.units?.total ?? 0,
    geofences: summary.watching?.geofences ?? 0,
    trips: summary.watching?.trips ?? 0,
    calls: {
      total,
      answered,
      answerRate: toAnswerRate(summary.calls?.answer_rate ?? null, total, answered),
    },
    cases: {
      total: summary.cases?.total ?? 0,
      open: summary.cases?.open ?? 0,
      resolvedWithoutCall: summary.cases?.resolved_without_call ?? 0,
      requiresHuman: summary.cases?.requires_human ?? 0,
    },
    narrative: toNarrative(summary.narrativa),
    panel: mapPanel(summary.panel),
  };
}
